// Workflow Copilot — applies the model's <edits> operations to a workflowState.
//
// Pure function: applyCopilotEdits(state, edits) returns { state, applied,
// errors } and never mutates the input. The caller decides whether to commit
// via setWorkflowState (which drives canvas re-render, dirty flag and undo).
//
// Ops (short keys are the compressed-workflow vocabulary from workflowCodec):
//   { op:'setWorkflowName', name }
//   { op:'updateNode', id, set:{ short-key fields } }   (set.pv merges)
//   { op:'deleteNode', id }                             (drops its connections)
//   { op:'addNode', ty, node:{ short-key fields } }
//   { op:'addConnection', connection:{ f, t, fa, ta, br, ... } }
//   { op:'updateConnection', id|from+to, set:{ short-key fields } }
//   { op:'deleteConnection', id|from+to }

var codec = require('./workflowCodec');

// Per-type identity fields, mirroring the planner's expandNode maps in
// useAutoBuildWorkflow.js (those live inside a closure, so a local copy).
var TYPE_META = {
  'start':        { templateId: 'sys-start', icon: 'start', subtitle: 'Entry point of the workflow', label: 'Start' },
  'variables':    { templateId: 'sys-variables', icon: 'variables', subtitle: 'Store typed context values', label: 'Variables' },
  'set-variable': { templateId: 'sys-set-variable', icon: 'set-variable', subtitle: 'Update a variable at runtime', label: 'Set Variable' },
  'condition':    { templateId: 'sys-condition', icon: 'condition', subtitle: 'Branch based on a condition', label: 'Condition' },
  'classifier':   { templateId: 'sys-classifier', icon: 'classifier', subtitle: 'Classify', label: 'Classify' },
  'router':       { templateId: 'sys-router', icon: 'router', subtitle: 'Route by exact match', label: 'Router' },
  'agent':        { templateId: 'sys-agent', icon: 'agent', subtitle: 'AI Agent', label: 'Agent' },
  'console-log':  { templateId: 'sys-console-log', icon: 'console-log', subtitle: 'Log output for debugging', label: 'Console Log' },
  'file-preview': { templateId: 'sys-file-preview', icon: 'file-preview', subtitle: 'Preview a file', label: 'File Preview' },
  'computer-use': { templateId: 'sys-computer-use', icon: 'computer-use', subtitle: 'Terminal agent', label: 'Computer Use' },
  'custom':       { templateId: null, icon: 'custom', subtitle: 'Custom node', label: 'Custom' }
};

function findNode(state, id) {
  return state.nodes.find(function(n) { return n.id === id; }) || null;
}

function nodeTitle(state, id) {
  var node = findNode(state, id);
  return node ? (node.title || node.label || id) : id;
}

function uniqueNodeId(state, base) {
  var id = base;
  var bump = 0;
  while (findNode(state, id)) {
    bump++;
    id = base + '-' + bump;
  }
  return id;
}

// Place a new node to the right of the current right-most node.
function autoPosition(state) {
  var best = null;
  state.nodes.forEach(function(n) {
    var left = (n.position && n.position.left) || 0;
    if (!best || left > (best.position && best.position.left || 0)) best = n;
  });
  if (!best) return { left: 120, top: 280 };
  return {
    left: ((best.position && best.position.left) || 0) + 350,
    top: (best.position && best.position.top) || 280
  };
}

// Merge an expanded `set` payload into a node. paramValues merges shallowly
// (so the model can change one value without resending them all); every other
// field replaces. label follows title when the two were in sync.
function mergeNodeSet(node, set) {
  var out = Object.assign({}, node);
  Object.keys(set).forEach(function(key) {
    if (key === 'id') return; // identity is never editable
    if (key === 'paramValues' && set.paramValues && typeof set.paramValues === 'object') {
      out.paramValues = Object.assign({}, node.paramValues || {}, set.paramValues);
      return;
    }
    out[key] = set[key];
  });
  if (set.title && node.label === node.title) out.label = set.title;
  return out;
}

function buildNode(state, op, sourceNode) {
  var ty = String(op.ty || 'custom');
  var meta = TYPE_META[ty];
  if (!meta) throw new Error('addNode: unknown node type "' + ty + '"');

  // decompressWorkflow expands short keys AND restores the standard defaults
  // (kind, conditionRule, paramValues, ...), same shape real nodes have.
  var spec = op.node && typeof op.node === 'object' ? op.node : {};
  var expanded = codec.decompressWorkflow({ _v: 1, n: [spec] }).nodes[0];

  // Explicit position wins; otherwise sit right of the source node the new
  // node attaches after; otherwise right of the whole graph.
  var position;
  if (spec.po || spec.position) {
    position = expanded.position;
  } else if (sourceNode) {
    var srcPos = sourceNode.position || { left: 0, top: 0 };
    position = { left: (srcPos.left || 0) + 350, top: srcPos.top || 280 };
  } else {
    position = autoPosition(state);
  }

  var ts = Date.now().toString(36);
  var title = expanded.title || meta.label;
  var node = Object.assign({}, expanded, {
    id: uniqueNodeId(state, expanded.id || ('copilot-' + ty + '-' + ts)),
    kind: 'workflow-node',
    nodeType: 'card',
    category: ty === 'custom' ? 'custom' : 'system',
    templateId: ty === 'custom' ? (expanded.templateId || ('custom-' + ts)) : meta.templateId,
    icon: ty === 'custom' ? (expanded.icon || 'custom') : meta.icon,
    subtitle: meta.subtitle,
    title: title,
    label: title,
    position: position
  });
  // Condition nodes need the "variable" hyperparameter for the variable chip.
  if (ty === 'condition' && (!Array.isArray(node.hyperparameters) || node.hyperparameters.length === 0)) {
    node.hyperparameters = [{ id: 'cond-var', key: 'variable', type: 'any', default: '' }];
  }
  return node;
}

// Connections can be addressed by id, or by { from, to } when the model has
// no id (compressed connections may omit it).
function matchConnection(conn, op) {
  if (op.id) return conn.id === op.id;
  return op.from && op.to && conn.from === op.from && conn.to === op.to;
}

function connLabel(state, conn) {
  return '"' + nodeTitle(state, conn.from) + '" → "' + nodeTitle(state, conn.to) + '"';
}

// --- Individual op handlers. Each returns { state, note } or throws. ---

function opSetWorkflowName(state, op) {
  var name = String(op.name || '').trim();
  if (!name) throw new Error('setWorkflowName: name is required');
  return {
    state: Object.assign({}, state, { workflowName: name }),
    note: 'Renamed workflow to "' + name + '"'
  };
}

function opUpdateNode(state, op) {
  var node = findNode(state, op.id);
  if (!node) throw new Error('updateNode: no node with id "' + op.id + '"');
  var set = codec.expandNodeKeys(op.set && typeof op.set === 'object' ? op.set : {});
  if (Object.keys(set).length === 0) throw new Error('updateNode: set is empty');
  if ('code' in set) throw new Error('updateNode cannot change code — use editNodeCode (opens AskCode on the node) instead.');
  var nodes = state.nodes.map(function(n) { return n.id === op.id ? mergeNodeSet(n, set) : n; });
  var note = 'Updated node "' + (node.title || op.id) + '" (' + Object.keys(set).join(', ') + ')';
  // Retargeting a set-variable must keep the variable contract intact.
  if (isSetVariableNode(node) && set.paramValues && set.paramValues.variable) {
    var declaration = buildMissingDeclaration(state, set.paramValues.variable);
    if (declaration) {
      nodes = nodes.concat([declaration]);
      note += '. Auto-added missing Variables node "' + declaration.title + '" (id: ' + declaration.id +
        ', dataType string) to declare it — updateNode its pv.dataType if it should be int/array/json/boolean.';
    }
  }
  return {
    state: Object.assign({}, state, { nodes: nodes }),
    note: note
  };
}

// BFS over connections from the Start node — the set of nodes that can ever
// run. A node whose only remaining inflow is a loop-back from its own
// downstream is NOT in this set (a loop-back alone can never start a loop).
function reachableFromStart(state) {
  var start = (state.nodes || []).find(function(n) { return n && n.icon === 'start'; });
  var seen = {};
  if (!start) return seen;
  var queue = [start.id];
  seen[start.id] = true;
  while (queue.length) {
    var id = queue.shift();
    (state.connections || []).forEach(function(c) {
      if (c && c.from === id && c.to && !seen[c.to]) { seen[c.to] = true; queue.push(c.to); }
    });
  }
  return seen;
}

// Loud warning when a delete just stranded nodes that were reachable before —
// "deleted the loop's entry edge" is invisible in a local diff but kills the
// whole downstream path.
function strandedNodesWarning(prevState, nextState, suggestion) {
  var before = reachableFromStart(prevState);
  var after = reachableFromStart(nextState);
  var stranded = (nextState.nodes || []).filter(function(n) {
    return n && n.icon !== 'variables' && before[n.id] && !after[n.id];
  }).map(function(n) { return '"' + (n.title || n.label || n.id) + '"'; });
  if (stranded.length === 0) return '';
  return 'CRITICAL: ' + stranded.join(', ') + (stranded.length > 1 ? ' are' : ' is') +
    ' now UNREACHABLE from Start and will NEVER run (a loop-back edge alone cannot start a loop). ' +
    'Restore an entry path NOW' + (suggestion ? ' (' + suggestion + ')' : '') + ' before doing anything else.';
}

function opDeleteNode(state, op) {
  var node = findNode(state, op.id);
  if (!node) throw new Error('deleteNode: no node with id "' + op.id + '"');
  // A Variables node is the variable's DECLARATION (type, default, retention).
  // Deleting it while other nodes still read/write that variable silently
  // breaks them at runtime — refuse instead of trusting the caller.
  if (isVariablesNode(node)) {
    var refs = findVariableReferents(state, node.title || node.label);
    if (refs.length > 0) {
      throw new Error('deleteNode: Variables node "' + (node.title || op.id) + '" is still used by ' + refs.join(', ') +
        ' — deleting the declaration breaks those reads/writes. Rewire or delete the referencing nodes first, or keep the variable.');
    }
  }
  var next = Object.assign({}, state, {
    nodes: state.nodes.filter(function(n) { return n.id !== op.id; }),
    connections: state.connections.filter(function(c) { return c.from !== op.id && c.to !== op.id; })
  });
  var note = 'Deleted node "' + (node.title || op.id) + '"';
  var warnings = danglingLabelWarnings(state, next);
  if (warnings.length > 0) note += '. ' + warnings.join(' ');
  var stranded = strandedNodesWarning(state, next, 'reconnect the survivors to their upstream');
  if (stranded) note += '. ' + stranded;
  return { state: next, note: note };
}

function isConditionNode(node) {
  return !!node && (node.templateId === 'sys-condition' || node.icon === 'condition');
}

function isVariablesNode(node) {
  return !!node && (node.templateId === 'sys-variables' || node.icon === 'variables');
}

function isSetVariableNode(node) {
  return !!node && (node.templateId === 'sys-set-variable' || node.icon === 'set-variable');
}

// Every way a workflow can read or write a variable by name: set-variable
// targets, "@Name" in any param value or condition rule, vars["Name"]/@Name in
// custom code and generation prompts. Returns human-readable referent labels.
function findVariableReferents(state, varName) {
  var name = String(varName || '').trim();
  if (!name) return [];
  var atRef = '@' + name;
  var referents = [];
  state.nodes.forEach(function(n) {
    if (isVariablesNode(n)) return;
    var how = null;
    var pv = n.paramValues || {};
    if (isSetVariableNode(n) && String(pv.variable || '').trim() === name) how = 'writes it';
    if (!how) {
      Object.keys(pv).forEach(function(key) {
        if (!how && typeof pv[key] === 'string' && pv[key].indexOf(atRef) !== -1) how = 'reads ' + atRef;
      });
    }
    if (!how && n.conditionRule && Array.isArray(n.conditionRule.conditions)) {
      n.conditionRule.conditions.forEach(function(c) {
        if (!how && c && typeof c.value === 'string' && c.value.indexOf(atRef) !== -1) how = 'tests ' + atRef;
      });
    }
    if (!how) {
      ['code', 'generationPrompt'].forEach(function(field) {
        var text = n[field];
        if (!how && typeof text === 'string' &&
            (text.indexOf('vars["' + name + '"]') !== -1 || text.indexOf("vars['" + name + "']") !== -1 || text.indexOf(atRef) !== -1)) {
          how = (field === 'code' ? 'its code' : 'its prompt') + ' reads it';
        }
      });
    }
    if (how) referents.push('"' + (n.title || n.id) + '" (' + how + ')');
  });
  return referents;
}

// A set-variable without a declared Variables node writes an untyped,
// non-retained variable that reviewers flag and later edits mis-handle. When
// the target is undeclared, build the missing Variables node so the caller can
// add it in the same edit (the platform contract: one Variables node per
// variable, titled with the variable name).
function buildMissingDeclaration(state, varName) {
  var name = String(varName || '').trim();
  if (!name) return null;
  var exists = state.nodes.some(function(n) {
    return isVariablesNode(n) && String(n.title || n.label || '').trim() === name;
  });
  if (exists) return null;
  return buildNode(state, { ty: 'variables', node: { t: name, pv: { dataType: 'string', value: '' } } }, null);
}

// Classifier/Router labels that had a consumer before an edit and none after —
// a dead-end that stalls loops. Surfaced as a warning on the edit's note so
// the model rewires instead of moving on.
function danglingLabelWarnings(prevState, nextState) {
  var warnings = [];
  prevState.nodes.forEach(function(n) {
    if (!isLabelBranchNode(n)) return;
    if (!nextState.nodes.some(function(m) { return m.id === n.id; })) return;
    nodeLabels(n).forEach(function(label, i) {
      var anchor = 'classifier-' + i;
      var had = prevState.connections.some(function(c) { return c.from === n.id && c.fromAnchor === anchor; });
      var has = nextState.connections.some(function(c) { return c.from === n.id && c.fromAnchor === anchor; });
      if (had && !has) {
        warnings.push('WARNING: "' + (n.title || n.id) + '" label "' + label + '" now has NO consumer — anything classified as "' + label +
          '" dead-ends there (in a loop, the loop stalls). Reconnect it: addConnection {f:"' + n.id + '", fa:"' + anchor + '", t:<consumer id>}.');
      }
    });
  });
  return warnings;
}

// Condition nodes have dedicated YES/NO output dots whose side depends on the
// node's INPUT anchor (same mapping the planner uses):
//   input ta="left" (common) → YES=top,  NO=right
//   input ta="top"           → YES=right, NO=bottom
function conditionBranchAnchor(state, conditionNode, branch) {
  var incoming = state.connections.find(function(c) { return c.to === conditionNode.id; });
  var ta = incoming && incoming.toAnchor ? incoming.toAnchor : 'left';
  if (ta === 'top') return branch === 'yes' ? 'right' : 'bottom';
  return branch === 'yes' ? 'top' : 'right';
}

function isLabelBranchNode(node) {
  return !!node && (node.templateId === 'sys-classifier' || node.icon === 'classifier' ||
    node.templateId === 'sys-router' || node.icon === 'router');
}

function nodeLabels(node) {
  return String(((node.paramValues || {}).labels) || '')
    .split(',').map(function(s) { return s.trim(); }).filter(Boolean);
}

// Classifier/Router outputs sit on per-label anchors ("classifier-N" in
// pv.labels order). Resolves the label arg to its anchor; REQUIRED when the
// source is a classifier/router so the connection never lands on a generic dot.
function resolveLabelAnchor(sourceNode, rawLabel) {
  var labels = nodeLabels(sourceNode);
  var wanted = String(rawLabel || '').trim().toLowerCase();
  var idx = labels.findIndex(function(l) { return l.toLowerCase() === wanted; });
  if (idx === -1) {
    throw new Error('"' + (sourceNode.title || sourceNode.id) + '" branches per label — pass label:"<one of: ' + labels.join(', ') + '>" to say which output the new node attaches to.');
  }
  return { anchor: 'classifier-' + idx, label: labels[idx] };
}

// Validates the branch arg for an addNode/attach against its source node:
// REQUIRED (yes/no) when the source is a Condition, forbidden elsewhere.
function resolveBranch(sourceNode, rawBranch) {
  var branch = String(rawBranch || '').trim().toLowerCase();
  if (isConditionNode(sourceNode)) {
    if (branch !== 'yes' && branch !== 'no') {
      throw new Error('"' + (sourceNode.title || sourceNode.id) + '" is a Condition node — pass branch:"yes" or branch:"no" to say which branch the new node attaches to.');
    }
    return branch;
  }
  return branch === 'yes' || branch === 'no' ? branch : null;
}

// A new node must join the flow: `after` names the source node it runs after
// and an auto-connection is created. Only Variables nodes may float detached.
function opAddNode(state, op) {
  var after = String(op.after || '').trim();
  var sourceNode = null;
  if (after) {
    sourceNode = findNode(state, after);
    if (!sourceNode) throw new Error('addNode: no node with id "' + after + '" to attach after');
  } else if (op.ty !== 'variables') {
    throw new Error('addNode: provide "after" — the id of the node this new node runs after (it gets connected automatically). Only ty "variables" may be added detached.');
  }
  var branch = null;
  var labeled = null; // { anchor, label } when the source is a classifier/router
  if (sourceNode) {
    if (isLabelBranchNode(sourceNode)) labeled = resolveLabelAnchor(sourceNode, op.label || op.branch);
    else branch = resolveBranch(sourceNode, op.branch);
  }

  var node = buildNode(state, op, sourceNode);
  // Branch adds without an explicit position: YES (top dot) goes up-right,
  // NO (right dot) drops slightly below the straight line.
  if (branch && !(op.node && (op.node.po || op.node.position))) {
    node = Object.assign({}, node, {
      position: {
        left: node.position.left,
        top: node.position.top + (branch === 'yes' ? -180 : 120)
      }
    });
  }
  var connections = state.connections;
  if (sourceNode) {
    var conn = {
      id: 'copilot-conn-' + Date.now().toString(36) + '-' + connections.length,
      from: sourceNode.id,
      to: node.id,
      fromAnchor: branch ? conditionBranchAnchor(state, sourceNode, branch)
        : labeled ? labeled.anchor : 'right',
      toAnchor: 'left',
      noWait: false,
      noData: false
    };
    if (branch) conn.branch = branch;
    connections = connections.concat([conn]);
  }
  var newNodes = [node];
  // The id is load-bearing: the model needs it for the next `after`/
  // addConnection, and the live overview can lag a round behind.
  var note = 'Added ' + (TYPE_META[op.ty] ? TYPE_META[op.ty].label : 'node') + ' "' + node.title + '" (id: ' + node.id + ')' +
    (sourceNode ? ', connected after "' + (sourceNode.title || sourceNode.id) + '"' +
      (branch ? ' (' + branch + ' branch)' : labeled ? ' ("' + labeled.label + '" output)' : '') : '');
  if (op.ty === 'set-variable') {
    var declaration = buildMissingDeclaration(state, (node.paramValues || {}).variable);
    if (declaration) {
      newNodes.push(declaration);
      note += '. Auto-added missing Variables node "' + declaration.title + '" (id: ' + declaration.id +
        ', dataType string) to declare it — updateNode its pv.dataType if it should be int/array/json/boolean.';
    }
  }
  return {
    state: Object.assign({}, state, { nodes: state.nodes.concat(newNodes), connections: connections }),
    note: note
  };
}

function opAddConnection(state, op) {
  var spec = op.connection && typeof op.connection === 'object' ? op.connection : {};
  var conn = codec.decompressWorkflow({ _v: 1, c: [spec] }).connections[0];
  var fromNode = findNode(state, conn.from);
  if (!fromNode) throw new Error('addConnection: no node with id "' + conn.from + '"');
  if (!findNode(state, conn.to)) throw new Error('addConnection: no node with id "' + conn.to + '"');
  conn.id = conn.id || ('copilot-conn-' + Date.now().toString(36) + '-' + state.connections.length);
  // Leaving a Condition node: branch is mandatory and picks the output dot
  // (unless the caller explicitly chose an anchor).
  if (isConditionNode(fromNode)) {
    conn.branch = resolveBranch(fromNode, conn.branch);
    if (!conn.fromAnchor) conn.fromAnchor = conditionBranchAnchor(state, fromNode, conn.branch);
  }
  // Leaving a Classifier/Router: outputs fire from per-label anchors
  // ("classifier-N"), so an edge on a generic dot NEVER fires at runtime —
  // the anchor is mandatory here, mirroring the Condition branch rule above.
  if (isLabelBranchNode(fromNode)) {
    var fa = String(conn.fromAnchor || '').trim();
    if (!fa && spec.label) {
      conn.fromAnchor = resolveLabelAnchor(fromNode, spec.label).anchor;
    } else {
      var labels = nodeLabels(fromNode);
      var anchorMatch = /^classifier-(\d+)$/.exec(fa);
      var validAnchor = fa === 'bottom' || (anchorMatch && Number(anchorMatch[1]) < labels.length);
      if (!validAnchor) {
        throw new Error('"' + (fromNode.title || fromNode.id) + '" branches per label — a connection leaving it must name the label output via fa, or it will NEVER fire. Re-issue with ' +
          labels.map(function(label, i) { return 'fa:"classifier-' + i + '" (' + label + ')'; }).join(', ') +
          ' or fa:"bottom" (no-match fallback).');
      }
    }
  }
  if (!conn.fromAnchor) conn.fromAnchor = 'right';
  if (!conn.toAnchor) conn.toAnchor = 'left';
  // Idempotent: asking for a connection that already exists is a no-op
  // success, NOT an error — error-driven replans made the model oscillate
  // (delete/re-add the same edges endlessly).
  var duplicate = state.connections.some(function(c) {
    return c.from === conn.from && c.to === conn.to && (c.fromAnchor || 'right') === conn.fromAnchor;
  });
  if (duplicate) {
    return { state: state, note: 'Connection ' + connLabel(state, conn) + ' already exists — nothing to do' };
  }
  var note = 'Connected ' + connLabel(state, conn);
  var bypass = branchBypassWarning(state, conn);
  if (bypass) note += '. ' + bypass;
  return {
    state: Object.assign({}, state, { connections: state.connections.concat([conn]) }),
    note: note
  };
}

// True if maybeAncestorId lies anywhere upstream of nodeId (following
// connections backwards; cycle-safe for loop-back workflows).
function isUpstreamOf(state, maybeAncestorId, nodeId) {
  var seen = {};
  var queue = [nodeId];
  while (queue.length > 0) {
    var cur = queue.shift();
    if (cur === maybeAncestorId) return true;
    if (seen[cur]) continue;
    seen[cur] = true;
    state.connections.forEach(function(c) {
      if (c.to === cur && !seen[c.from]) queue.push(c.from);
    });
  }
  return false;
}

// A branch target fed directly from a node UPSTREAM of its gate (condition /
// classifier / router) runs on EVERY payload — the new edge silently disables
// the branch. Warn loudly at creation time; the prompt alone can't be trusted
// to keep this invariant.
function branchBypassWarning(state, conn) {
  var gateEdge = state.connections.find(function(c) {
    if (c.to !== conn.to || c.from === conn.from) return false;
    var src = findNode(state, c.from);
    if (!src || !(isConditionNode(src) || isLabelBranchNode(src))) return false;
    return isUpstreamOf(state, conn.from, c.from);
  });
  if (!gateEdge) return '';
  var gate = findNode(state, gateEdge.from) || {};
  var target = findNode(state, conn.to) || {};
  return 'WARNING: "' + (target.title || conn.to) + '" is a branch target of "' + (gate.title || gateEdge.from) +
    '", and this new edge comes from a node UPSTREAM of that gate — it BYPASSES the branch, so the target will now run for EVERY payload, not only its branch. ' +
    'If you only meant to pass data forward, deleteConnection this edge and use a Set Variable upstream + vars["..."] in the target instead.';
}

function opUpdateConnection(state, op) {
  var target = state.connections.find(function(c) { return matchConnection(c, op); });
  if (!target) throw new Error('updateConnection: connection not found');
  var set = codec.expandConnKeys(op.set && typeof op.set === 'object' ? op.set : {});
  if (Object.keys(set).length === 0) throw new Error('updateConnection: set is empty');
  delete set.id;
  return {
    state: Object.assign({}, state, {
      connections: state.connections.map(function(c) {
        return c === target ? Object.assign({}, c, set) : c;
      })
    }),
    note: 'Updated connection ' + connLabel(state, target)
  };
}

function opDeleteConnection(state, op) {
  var target = state.connections.find(function(c) { return matchConnection(c, op); });
  if (!target) throw new Error('deleteConnection: connection not found');
  var next = Object.assign({}, state, {
    connections: state.connections.filter(function(c) { return c !== target; })
  });
  var note = 'Removed connection ' + connLabel(state, target);
  var warnings = danglingLabelWarnings(state, next);
  if (warnings.length > 0) note += '. ' + warnings.join(' ');
  var stranded = strandedNodesWarning(state, next,
    'e.g. addConnection {"f":"' + target.from + '","t":"' + target.to + '"} to undo this delete');
  if (stranded) note += '. ' + stranded;
  return { state: next, note: note };
}

var OP_HANDLERS = {
  setWorkflowName: opSetWorkflowName,
  updateNode: opUpdateNode,
  deleteNode: opDeleteNode,
  addNode: opAddNode,
  addConnection: opAddConnection,
  updateConnection: opUpdateConnection,
  deleteConnection: opDeleteConnection
};

// --- Public API ---

function applyCopilotEdits(state, edits) {
  var current = Object.assign({}, state, {
    nodes: Array.isArray(state && state.nodes) ? state.nodes : [],
    connections: Array.isArray(state && state.connections) ? state.connections : []
  });
  var applied = [];
  var errors = [];

  (Array.isArray(edits) ? edits : []).forEach(function(op, idx) {
    var handler = op && OP_HANDLERS[op.op];
    if (!handler) {
      errors.push('Edit #' + (idx + 1) + ': unknown operation "' + (op && op.op) + '"');
      return;
    }
    try {
      var result = handler(current, op);
      current = result.state;
      applied.push(result.note);
    } catch (err) {
      errors.push('Edit #' + (idx + 1) + ': ' + err.message);
    }
  });

  return { state: current, applied: applied, errors: errors };
}

module.exports = {
  applyCopilotEdits: applyCopilotEdits,
  // Shared with workflowCopilotTools so the custom/agent addNode path applies
  // the same classifier/router label contract as system addNode.
  isLabelBranchNode: isLabelBranchNode,
  resolveLabelAnchor: resolveLabelAnchor
};
